import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { SCHEDULE } from "@/constants/testIds";
import { t, localizeReason } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CalendarClock, Sparkles, TrendingUp, CheckCircle2, Pencil } from "lucide-react";

const NONE = "__none__";

function groupByDay(items) {
  const groups = {};
  for (const it of items) {
    const d = new Date(it.scheduled_at);
    const key = d.toISOString().slice(0, 10);
    (groups[key] = groups[key] || []).push(it);
  }
  return Object.entries(groups).sort(([a], [b]) => (a < b ? -1 : 1));
}

function isoToLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Schedule() {
  const [list, setList] = useState([]);
  const [videos, setVideos] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ video_id: "", account_id: "", device_id: null, scheduled_at: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [suggBusy, setSuggBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, v, a, d] = await Promise.all([api.schedule(), api.videos(), api.accounts(), api.devices()]);
      setList(s); setVideos(v); setAccounts(a); setDevices(d);
    } catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => groupByDay(list), [list]);

  const videoFor = (id) => videos.find((v) => v.id === id);
  const accountFor = (id) => accounts.find((a) => a.id === id);
  const deviceFor = (id) => devices.find((d) => d.id === id);

  const openNew = () => {
    setEditing(null);
    setForm({ video_id: "", account_id: "", device_id: null, scheduled_at: "" });
    setSuggestions([]);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      video_id: it.video_id || "",
      account_id: it.account_id || "",
      device_id: it.device_id || null,
      scheduled_at: isoToLocalInput(it.scheduled_at),
    });
    if (it.account_id) fetchSuggestions(it.account_id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.video_id || !form.account_id || !form.scheduled_at) return toast.error("Заполни все поля");
    const payload = {
      video_id: form.video_id,
      account_id: form.account_id,
      device_id: form.device_id || null,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
    };
    try {
      if (editing) await api.updateSchedule(editing, payload);
      else await api.createSchedule(payload);
      toast.success(t.common.saved);
      setOpen(false);
      setEditing(null);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.deleteSchedule(id); load(); }
    catch { toast.error(t.common.failed); }
  };

  const markDone = async (id) => {
    try {
      await api.markScheduleDone(id);
      toast.success(t.schedule.marked);
      load();
    } catch { toast.error(t.common.failed); }
  };

  const fetchSuggestions = async (accountId) => {
    if (!accountId) return;
    setSuggBusy(true);
    setSuggestions([]);
    try {
      const r = await api.bestTimes(accountId, 3);
      setSuggestions(r.slots || []);
    } catch { toast.error(t.common.failed); }
    finally { setSuggBusy(false); }
  };

  const applySuggestion = (iso) => setForm((f) => ({ ...f, scheduled_at: isoToLocalInput(iso) }));

  return (
    <div data-testid={SCHEDULE.root}>
      <PageHeader
        title={t.schedule.title}
        description={t.schedule.description}
        actions={
          <button
            data-testid={SCHEDULE.addBtn}
            onClick={openNew}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" /> {t.schedule.add}
          </button>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <CalendarClock className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.schedule.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.schedule.emptyHint}</div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([day, items]) => (
            <div key={day}>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                {new Date(day).toLocaleDateString("ru-RU", { weekday: "long", month: "short", day: "numeric" })}
              </div>
              <div className="space-y-2">
                {items.map((it) => {
                  const v = videoFor(it.video_id);
                  const a = accountFor(it.account_id);
                  const d = deviceFor(it.device_id);
                  const canMarkDone = it.status === "planned" || it.status === "running";
                  return (
                    <div key={it.id} className="card-soft p-4 flex items-center gap-4">
                      <div className="text-emerald-600 font-display text-lg font-semibold w-16 shrink-0">
                        {new Date(it.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                        {v?.thumbnail_url && <img src={v.thumbnail_url} className="w-full h-full object-cover" alt="" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{v?.filename || "(видео удалено)"}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {a?.username || "?"} · {d?.name || "любое устройство"}
                        </div>
                      </div>
                      <StatusBadge status={it.status} />
                      {canMarkDone && (
                        <button
                          data-testid={SCHEDULE.markDoneBtn(it.id)}
                          onClick={() => markDone(it.id)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium transition-colors"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Опубл.
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(it)}
                        className="text-slate-400 hover:text-emerald-600 transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        data-testid={SCHEDULE.deleteBtn(it.id)}
                        onClick={() => del(it.id)}
                        className="text-slate-300 hover:text-rose-500 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.schedule.edit : t.schedule.add}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.schedule.fields.video}</Label>
              <Select value={form.video_id} onValueChange={(v) => setForm({ ...form, video_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.none} /></SelectTrigger>
                <SelectContent>
                  {videos.map((v) => <SelectItem key={v.id} value={v.id}>{v.filename}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.schedule.fields.account}</Label>
              <Select
                value={form.account_id}
                onValueChange={(v) => { setForm({ ...form, account_id: v }); fetchSuggestions(v); }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.none} /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.schedule.fields.device}</Label>
              <Select
                value={form.device_id || NONE}
                onValueChange={(v) => setForm({ ...form, device_id: v === NONE ? null : v })}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="любое" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>любое</SelectItem>
                  {devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.schedule.fields.when}</Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1" />
              {form.account_id && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">
                    <span className="inline-flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> {t.schedule.bestTimes}
                    </span>
                    {suggBusy && <span className="normal-case text-slate-400 text-xs">{t.common.loading}</span>}
                  </div>
                  {suggestions.length === 0 && !suggBusy ? (
                    <div className="text-xs text-slate-400">{t.schedule.bestTimesPickAccount}</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {suggestions.map((s) => {
                        const d = new Date(s.at);
                        const label = d.toLocaleString("ru-RU", { weekday: "short", hour: "2-digit", minute: "2-digit" });
                        const reason = localizeReason(s.reason);
                        return (
                          <button
                            key={s.at}
                            type="button"
                            onClick={() => applySuggestion(s.at)}
                            className="group inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-emerald-50 hover:bg-emerald-500 hover:text-white text-emerald-700 transition-colors"
                            title={reason}
                          >
                            <Sparkles className="h-3 w-3" />
                            {label}
                            <span className="opacity-60 group-hover:opacity-100">· {reason}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={SCHEDULE.saveBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.common.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
